import { api } from "@/lib/api";
import { isIos, isStandalone } from "@/lib/pwa";
import { base64UrlToBytes, deviceLabel } from "@/lib/push-utils";
import type { PushDevice } from "@/lib/types";

/**
 * This browser's half of web push: whether it can, asking for permission, and
 * handing the server its subscription. The service worker (src/sw/sw.ts) shows what
 * arrives. Permission must be asked from a tap — every browser ignores it otherwise —
 * so `enablePush` is only ever called from a click handler.
 */
export type PushAvailability =
  /** No service worker or no Push API: an old browser, or Firefox in private mode. */
  | "unsupported"
  /** iOS/iPadOS outside the installed app: Safari delivers push to Home Screen apps only. */
  | "needs-install"
  | "available";

export function pushAvailability(): PushAvailability {
  const capable = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (isIos() && !isStandalone()) return "needs-install";
  return capable ? "available" : "unsupported";
}

export const notificationPermission = (): NotificationPermission =>
  "Notification" in window ? Notification.permission : "denied";

async function registration(): Promise<ServiceWorkerRegistration> {
  // `ready` never settles without a worker (the dev server has none), so give up after a while.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error("The app's service worker is not running here.")), 8000)),
  ]);
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushAvailability() !== "available") return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

const save = (subscription: PushSubscription) =>
  api.savePushSubscription({ ...subscription.toJSON(), label: deviceLabel(navigator.userAgent, navigator.maxTouchPoints) });

/**
 * Asks for permission (call from a tap), subscribes and saves the subscription.
 * Returns null when the person said no.
 */
export async function enablePush(publicKey: string): Promise<PushDevice | null> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;
  const reg = await registration();
  const key = base64UrlToBytes(publicKey);
  let subscription = await reg.pushManager.getSubscription();
  // A subscription made under another key cannot receive this server's pushes.
  const sameKey = (() => {
    const existing = subscription?.options.applicationServerKey;
    if (!existing) return false;
    const a = new Uint8Array(existing);
    return a.length === key.length && a.every((byte, i) => byte === key[i]);
  })();
  if (subscription && !sameKey) {
    await subscription.unsubscribe().catch(() => undefined);
    subscription = null;
  }
  subscription ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  return (await save(subscription)).device;
}

/**
 * Which saved device is this browser: re-saving the subscription it holds returns its
 * row (and heals a row deleted on the server). Null when it holds none.
 */
export async function syncThisDevice(): Promise<PushDevice | null> {
  if (notificationPermission() !== "granted") return null;
  const subscription = await currentSubscription();
  return subscription ? (await save(subscription)).device : null;
}

/** Turns push off on this browser: the browser's subscription and the server's row. */
export async function disableThisDevice(deviceId: string | null): Promise<void> {
  const subscription = await currentSubscription();
  await subscription?.unsubscribe().catch(() => undefined);
  if (deviceId) await api.removePushDevice(deviceId).catch(() => undefined);
}
